<script setup lang="ts">
import EventTable from "@/components/modules/EventJournal/EventTable.vue";
import {useEventLogStore} from "@/stores/event-log";
import {onMounted, computed, ref} from "vue";
import {storeToRefs} from "pinia";

const eventLogStore = useEventLogStore();

// Используем реактивные данные из store
const {eventsLog} = storeToRefs(eventLogStore);

// Количество элементов на странице
const limit = 20;

// Флаг загрузки данных
const { isLoadingEventsLog } = storeToRefs(eventLogStore);

// Значение поисковой строки
const eventSearchString = ref("");

// Вычисляемые свойства для paginator и списка событий
const paginator = computed(() => {
  if (!eventsLog.value || typeof eventsLog.value !== "object") {
    return {
      current_page: null,
      next_page: null,
      previous_page: null,
      total_items: 0,
      total_pages: 0
    };
  }

  return {
    current_page: eventsLog.value.current_page || null,
    next_page: eventsLog.value.next_page || null,
    previous_page: eventsLog.value.previous_page || null,
    total_items: eventsLog.value.total_items || 0,
    total_pages: eventsLog.value.total_pages || 0
  };
});

const eventLogList = computed(() => {
  if (!eventsLog.value || typeof eventsLog.value !== "object") {
    return [];
  }
  return eventsLog.value.results || [];
});

// Функция загрузки данных
const loadEventsLog = async (page = 1) => {
  try {
    isLoadingEventsLog.value = true;
    await eventLogStore.getEventsLogQuery(page, limit, eventSearchString.value);
  } catch (error) {
    console.error("Ошибка загрузки журнала событий:", error);
  } finally {
    isLoadingEventsLog.value = false;
  }
};

// Обработчик перехода на страницу
const handlePageChange = (page: number) => {
  loadEventsLog(page);
};

onMounted(() => {
  loadEventsLog(1);
});

let debounceTimer: number | null = null;

const loadEventsLogsDebounced = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    loadEventsLog(1);
  }, 300); // 300мс задержка
};

</script>

<template>
  <div class="event-log">
    <div class="event-log__header">
      <h1 class="event-log__title">
        Журнал событий
      </h1>
      <InputSearchPrimary
        v-model="eventSearchString"
        size="large"
        placeholder="Найти"
        @update:model-value="loadEventsLogsDebounced"
      />
    </div>
    <EventTable
      :items="eventLogList"
      :paginator="paginator"
      :loading="isLoadingEventsLog"
      class="event-log__table"
      @page-change="handlePageChange"
    />
  </div>
</template>

<style scoped lang="scss">
.event-log {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  overflow: hidden;

  &__header {
    display: flex;
    justify-content: space-between;
    flex-shrink: 0;
  }

  &__title {
    font-weight: 500;
    font-size: 24px;
    line-height: 26px;
    color: var(--text-text-main);
  }

  &__table {
    flex: 1;
    min-height: 0;
  }
}
</style>